import { BannerExtension } from "./extension";
import { PreferencesHost, loadPreferences } from "./prefs-loader";
import { makeDraggable } from "./reopen-position";
import {
  BannerSettings,
  BannerTexts,
  ConsentAction,
  NECESSARY,
  PublicBranding,
  PublicCookie,
} from "./types";

/** Where "Powered by BerryCookie" leads; the UTM tags let BerryCookie count sign-ups that come from banners. */
export const BRANDING_URL =
  "https://berrycookie.com/?utm_source=banner&utm_medium=free&utm_campaign=poweredby";

export interface BannerCallbacks {
  onDecision(action: ConsentAction, categories: string[]): void;
}

/**
 * The consent banner and the small re-open button, rendered in a shadow root so the customer's CSS cannot break
 * them and ours cannot leak into the page. The preference center comes from bc-prefs.js (see prefs/panel.ts), loaded
 * once the first layer is on screen. All texts are set with textContent, never as HTML.
 */
export class BannerView implements PreferencesHost {
  private readonly host: HTMLElement;
  private readonly root: ShadowRoot;
  private current: HTMLElement | null = null;
  private stopDragging: (() => void) | null = null;
  private readonly styles: string[] = [];

  constructor(
    readonly doc: Document,
    readonly settings: BannerSettings,
    readonly texts: BannerTexts,
    private readonly callbacks: BannerCallbacks,
    readonly cookies: PublicCookie[] = [],
    readonly language = "en",
    readonly extension: BannerExtension | null = null,
    private readonly branding: PublicBranding | null = null,
    /** Where bc.js was loaded from; bc-prefs.js is loaded from the same place. */
    private readonly scriptUrl: string | null = null,
  ) {
    this.host = doc.createElement("div");
    this.host.id = "berrycookie-root";
    this.root = this.host.attachShadow({ mode: "open" });
    const style = doc.createElement("style");
    // The shadow root resets inherited styles, so "same font as the site" is read from the page.
    const pageFont = doc.body
      ? doc.defaultView?.getComputedStyle(doc.body).fontFamily
      : "";
    style.textContent =
      buildCss(settings, pageFont || undefined) + (extension?.css ?? "");
    this.root.appendChild(style);
  }

  showBanner(): void {
    const { layout, behavior } = this.settings;
    const t = this.texts;
    const dialog = this.el("div", `bc-dialog bc-${layout}`);
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", layout === "popup" ? "true" : "false");
    dialog.setAttribute("aria-labelledby", "bc-title");
    dialog.setAttribute("aria-describedby", "bc-message");

    const body = this.el("div", "bc-body");
    const title = this.el("div", "bc-title", t.title);
    title.id = "bc-title";
    const message = this.el("div", "bc-message", t.message);
    message.id = "bc-message";
    if (this.policyUrl()) {
      message.appendChild(this.doc.createTextNode(" "));
      message.appendChild(this.privacyLink());
    }
    body.append(title, message);
    const extra = this.extension?.firstLayer(this.doc, () =>
      this.showPreferences(),
    );
    if (extra) {
      body.appendChild(extra);
    }

    const buttons = this.buttonRow();
    if (behavior.showSettings) {
      // On a wide bar "Settings" keeps its own width in front of the two equal choices.
      buttons.classList.add("bc-lead");
      buttons.appendChild(
        this.button(t.settings, "bc-secondary bc-extra", () =>
          this.showPreferences(),
        ),
      );
    }
    if (behavior.showRejectAll) {
      buttons.appendChild(
        this.button(t.rejectAll, this.rejectClass(), () =>
          this.decide("RejectAll", this.required()),
        ),
      );
    }
    buttons.appendChild(
      this.button(t.acceptAll, this.acceptClass(), () =>
        this.decide("AcceptAll", this.all()),
      ),
    );

    const content = this.el("div", "bc-content");
    content.append(body, buttons);
    dialog.appendChild(content);
    this.addBranding(dialog);
    this.present(dialog, layout === "popup");
    this.extension?.uiShown();
    // The preference center loads after the first layer is on screen, so a click on "Settings" opens it at once.
    if (behavior.showSettings || this.extension) {
      this.doc.defaultView?.setTimeout(() => this.preloadPreferences(), 0);
    }
  }

  /**
   * Per-category choices; opened from the banner or later from the re-open button. Opens at once when bc-prefs.js is
   * loaded, otherwise as soon as it arrives; when it cannot be loaded, the first layer is shown instead.
   */
  showPreferences(accepted?: string[], consentId?: string): void {
    const win = this.doc.defaultView;
    const ready = win?.__berrycookiePrefs;
    if (ready) {
      ready.render(this, accepted, consentId);
      return;
    }
    if (win) {
      void loadPreferences(win, this.doc, this.scriptUrl).then((module) =>
        module ? module.render(this, accepted, consentId) : this.showBanner(),
      );
    }
  }

  /** Starts loading bc-prefs.js in the background (no-op once loaded). */
  preloadPreferences(): void {
    const win = this.doc.defaultView;
    if (win && !win.__berrycookiePrefs) {
      void loadPreferences(win, this.doc, this.scriptUrl);
    }
  }

  /** Adds styles to the shadow root, once per style sheet. */
  addStyle(css: string): void {
    if (this.styles.indexOf(css) < 0) {
      this.styles.push(css);
      const style = this.doc.createElement("style");
      style.textContent = css;
      this.root.appendChild(style);
    }
  }

  /**
   * Small round button so visitors can change their mind at any time (GDPR art. 7(3)). Visitors can drag it to
   * the other side or higher up when it covers something; the place is remembered on their device.
   */
  showReopenButton(onClick: () => void): void {
    const button = this.el("button", "bc-reopen") as HTMLButtonElement;
    button.type = "button";
    button.setAttribute("aria-label", this.texts.settings);
    button.title = this.texts.settings;
    button.innerHTML = COOKIE_ICON;
    button.addEventListener("click", onClick);
    // A returning visitor is about to open the preferences: fetch them on the first hover or focus.
    const preload = () => this.preloadPreferences();
    button.addEventListener("pointerenter", preload, { once: true });
    button.addEventListener("focus", preload, { once: true });
    this.present(button, false);
    const win = this.doc.defaultView;
    if (win) {
      this.stopDragging = makeDraggable(button, win);
    }
  }

  hide(): void {
    this.stopDragging?.();
    this.stopDragging = null;
    this.current?.remove();
    this.current = null;
    this.root.querySelector(".bc-overlay")?.remove();
  }

  present(element: HTMLElement, modal: boolean): void {
    this.hide();
    this.mount();
    if (modal) {
      this.root.appendChild(this.el("div", "bc-overlay"));
    }
    this.root.appendChild(element);
    this.current = element;
    if (modal) {
      element
        .querySelector<HTMLElement>("button, input:not([disabled])")
        ?.focus();
    }
  }

  private mount(): void {
    if (this.host.isConnected) {
      return;
    }
    const attach = () => this.doc.body.appendChild(this.host);
    if (this.doc.body) {
      attach();
    } else {
      this.doc.addEventListener("DOMContentLoaded", attach, { once: true });
    }
  }

  /** fromSwitches: saved from the preferences, so a framework reads its own switches too. */
  decide(
    action: ConsentAction,
    categories: string[],
    fromSwitches = false,
  ): void {
    this.hide();
    this.extension?.decide(action, fromSwitches);
    this.callbacks.onDecision(action, categories);
  }

  /**
   * "Reject all" and "Accept all" always get the same size, row and font (.bc-choice, two equal grid columns);
   * the button style only changes colors: balanced fills both with the primary colors, emphasizeAccept outlines
   * "Reject all" in the banner's text color.
   */
  buttonRow(): HTMLElement {
    return this.el(
      "div",
      this.settings.behavior.showRejectAll ? "bc-buttons" : "bc-buttons bc-single",
    );
  }

  rejectClass(): string {
    return this.settings.theme.buttonStyle === "balanced"
      ? "bc-primary bc-choice"
      : "bc-outline bc-choice";
  }

  acceptClass(): string {
    return "bc-primary bc-choice";
  }

  all(): string[] {
    return this.settings.categories
      .filter((c) => c.enabled || c.required)
      .map((c) => c.key);
  }

  required(): string[] {
    const required = this.settings.categories
      .filter((c) => c.required)
      .map((c) => c.key);
    return required.indexOf(NECESSARY) >= 0
      ? required
      : [NECESSARY, ...required];
  }

  /**
   * The cookie/privacy policy in the visitor's language, else the banner-wide one. Only http(s) addresses are used
   * (the API validates them too), so a link can never run script.
   */
  policyUrl(): string | null {
    const url =
      this.texts.privacyPolicyUrl || this.settings.behavior.privacyPolicyUrl;
    return url && /^https?:\/\//i.test(url) ? url : null;
  }

  privacyLink(): HTMLAnchorElement {
    const link = this.el(
      "a",
      "bc-link",
      this.texts.privacyPolicy,
    ) as HTMLAnchorElement;
    link.href = this.policyUrl() ?? "#";
    link.target = "_blank";
    link.rel = "noopener";
    return link;
  }

  /**
   * The free plan's link (always shown there, the server enforces it); paid plans can hide it. A white-labelled banner
   * credits the agency instead, and only with an https link.
   */
  addBranding(dialog: HTMLElement): void {
    if (this.settings.behavior.showBranding) {
      const agency =
        this.branding && /^https:\/\//i.test(this.branding.url)
          ? this.branding
          : null;
      const branding = this.el(
        "a",
        "bc-branding",
        `Powered by ${agency ? agency.name : "BerryCookie"}`,
      ) as HTMLAnchorElement;
      branding.href = agency ? agency.url : BRANDING_URL;
      branding.target = "_blank";
      branding.rel = "noopener";
      dialog.appendChild(branding);
    }
  }

  button(
    label: string,
    className: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const button = this.el(
      "button",
      `bc-button ${className}`,
      label,
    ) as HTMLButtonElement;
    button.type = "button";
    button.addEventListener("click", onClick);
    return button;
  }

  el(tag: string, className: string, text?: string): HTMLElement {
    const element = this.doc.createElement(tag);
    element.className = className;
    if (text !== undefined) {
      element.textContent = text;
    }
    return element;
  }
}

const COOKIE_ICON =
  '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Zm-3.5 7a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm7 5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm-6 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/></svg>';

/** All styles live in the shadow root; colors come from the banner theme. */
export function buildCss(
  settings: BannerSettings,
  pageFont = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
): string {
  const { theme, layout, position } = settings;
  const r = theme.radius;
  const buttonRadius = Math.min(r, 10);
  const font = theme.fontFamily === "inherit" ? pageFont : theme.fontFamily;
  const placement =
    layout === "bar"
      ? `left:0;right:0;${position === "top" ? `top:0;border-radius:0 0 ${r}px ${r}px` : `bottom:0;border-radius:${r}px ${r}px 0 0`}`
      : layout === "corner"
        ? `bottom:16px;${position === "bottom-left" ? "left:16px" : "right:16px"};width:min(360px,calc(100vw - 32px));border-radius:${r}px`
        : "";

  return `:host{all:initial}*{box-sizing:border-box}.bc-dialog{position:fixed;z-index:2147483000;background:${theme.background};color:${theme.text};font-family:${font};font-size:14px;line-height:1.5;padding:20px 24px;box-shadow:0 10px 40px rgba(0,0,0,.2);max-height:90vh;overflow:auto;-webkit-font-smoothing:antialiased}.bc-bar,.bc-corner{${placement}}.bc-popup{left:50%;top:50%;transform:translate(-50%,-50%);width:min(480px,calc(100vw - 32px));border-radius:${r}px}.bc-overlay{position:fixed;inset:0;z-index:2147482999;background:rgba(0,0,0,.4)}.bc-content{display:flex;flex-direction:column;gap:14px}.bc-bar .bc-content{flex-direction:row;align-items:center;gap:24px;max-width:1200px;margin:0 auto}.bc-bar .bc-body{flex:1;min-width:0}.bc-title{font-size:16px;font-weight:700;margin:0 0 4px}.bc-message{opacity:.92}.bc-buttons{display:grid;grid-template-columns:1fr 1fr;gap:8px}.bc-single{grid-template-columns:1fr}.bc-extra{grid-column:1/-1;order:1}.bc-button{font:inherit;font-size:14px;font-weight:600;border:0;border-radius:${buttonRadius}px;padding:10px 18px;cursor:pointer;min-width:0;overflow-wrap:break-word}.bc-button:focus-visible,.bc-reopen:focus-visible,.bc-switch:focus-visible,.bc-link:focus-visible{outline:2px solid ${theme.primary};outline-offset:2px}.bc-primary{background:${theme.primary};color:${theme.primaryText}}.bc-secondary{background:${theme.secondary};color:${theme.secondaryText}}.bc-outline{background:transparent;color:${theme.text};box-shadow:inset 0 0 0 2px ${theme.text}b3}.bc-button:hover{filter:brightness(.95)}.bc-link,.bc-branding{color:inherit}.bc-branding{display:block;margin-top:10px;font-size:11px;opacity:.55;text-align:right;text-decoration:none}.bc-reopen{position:fixed;z-index:2147482998;bottom:16px;left:16px;width:44px;height:44px;border-radius:50%;border:0;display:flex;align-items:center;justify-content:center;cursor:pointer;background:${theme.primary};color:${theme.primaryText};box-shadow:0 4px 14px rgba(0,0,0,.25);touch-action:none}.bc-reopen.bc-dragging{cursor:grabbing;box-shadow:0 8px 24px rgba(0,0,0,.3)}.bc-reopen.bc-snap{transition:left .2s ease,bottom .2s ease}@media (prefers-reduced-motion:reduce){.bc-reopen.bc-snap{transition:none}}@media (min-width:641px){.bc-bar .bc-buttons{grid-template-columns:none;grid-auto-flow:column;grid-auto-columns:1fr}.bc-bar .bc-lead{grid-template-columns:auto}.bc-bar .bc-extra{grid-column:auto;order:0}}@media (max-width:640px){.bc-bar .bc-content{flex-direction:column;align-items:stretch;gap:14px}}`;
}
